import React, { useEffect, useRef, useState } from 'react';
import s from './BookingCheckout.module.scss';
import useIsAuth from 'sdk/hooks/useIsAuth';
import AddressCard from 'modules/shop/pages/Checkout/AddressCard';
import AddNewAddressForm from 'modules/shop/pages/Checkout/AddNewAddressForm';
import Button from 'components/shared/Button';
import { createInvoice } from 'sdk/hooks/payments/useCreateInvoice';
import { useGetMyUser } from 'sdk/hooks/user/useGetMyUser';
import useGetShippingAddress from 'sdk/hooks/shop/useGetShippingAddress';
import useUpdateShippingAddressById from 'sdk/hooks/useUpdateShippingAddressById';
import useAddShippingAddress from 'sdk/hooks/useAddShippingAddress';
import Promocode from './Promocode';
import { useNavigate } from 'react-router-dom';

const subtotals = (title, price) => {
  return (
    <div className={s.checkout_subtotal_item}>
      <div className={s.checkout_subtotal_title}>{title}</div>
      <div className={s.checkout_subtotal_price}>﷼{price}</div>
    </div>
  );
};

const BookingCheckout = ({
  price,
  teamId,
  selectedTime,
  selectedEndTime,
  duration,
  date,
  selectedServiceHistory,
  bookingTransactions,
  homeLocation,
  salonId,
  setBookingItemsWithDiscount,
  bookingItemsWithDiscount,
}: any) => {
  const firstVisit = useRef<boolean>(true);
  const navigate = useNavigate();

  const { data: isAuthData } = useIsAuth();
  const userId = isAuthData?.user?.id;
  const { data: userData } = useGetMyUser(userId || '');
  const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
  const [editingShippingAddressId, setEditingShippingAddressId] = useState(null);
  const [isAddBillingAddress, setIsAddBillingAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { data: shippingAddresses, refetch: refetchShippingAddresses } =
    useGetShippingAddress(userId);

  const { mutate: updateShippingAddress } = useUpdateShippingAddressById();
  const { mutate: addShippingAddress } = useAddShippingAddress();

  const handleSelectBillingAddress = id => {
    if (editingShippingAddressId) {
      return;
    }
    setSelectedShippingAddress(id);
  };

  useEffect(() => {
    if (firstVisit.current === true && !!userData) {
      if (!userData.phone) {
        alert('You dont have your phone please create it');
      }
    }
    firstVisit.current = false;
  }, [userData]);

  const handleBuy = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const generatedNumber = Math.floor(100000 + Math.random() * 900000);

      const totalPrice = bookingItemsWithDiscount.reduce(
        (sum, item) => sum + (item.discountedPrice ?? item.price),
        0,
      );

      const bookedTimes = bookingTransactions.reduce((acc, item) => {
        acc[`teamId_${item.team_id}`] = {
          start_time: item.start_time,
          end_time: item.end_time,
          duration: item.duration,
          date: item.date,
        };
        return acc;
      }, {});

      const response = await createInvoice({
        order: 'Booking',
        totalPrice: totalPrice * 100,
        email: userData?.email,
        fullName: userData?.username,
        user_id: userId,
        phoneNumber: '+' + userData?.phone,
        description: 'Booking',
        teamId,
        selectedTime: bookingTransactions?.[0]?.start_time,
        date: bookingTransactions?.[0]?.date,
        duration: bookingTransactions?.[0]?.duration,
        selectedEndTime,
        selectedService: selectedServiceHistory[0],
        service_ids: { serviceIds: selectedServiceHistory },
        booked_times: bookedTimes,
        home_location: homeLocation,
        record_id: generatedNumber,
        salonId,
      });

      const res = response;

      if (!res || typeof res !== 'string') {
        console.error('  Invalid invoice URL:', res);
        throw new Error('Failed to create invoice - no valid link in response');
      }

      window.location.href = res;
    } catch (err) {
      console.error(' Error creating invoice:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const subtotal = bookingItemsWithDiscount.reduce((sum, item) => {
    return sum + (item.discountedPrice ?? item.price);
  }, 0);

  const discount = bookingItemsWithDiscount.reduce((sum, item) => {
    const saved = item.price && item.discountedPrice ? item.price - item.discountedPrice : 0;
    return sum + saved;
  }, 0);

  const isUserDataComplete =
    userData?.username && userData?.email && userData?.phone && userData?.id;

  return (
    <>
      <div className={s.checkout}>
        <div className={s.addresses} style={{ opacity: 0, height: 0, zIndex: -1 }}>
          <div className={s.address_card_top}>
            <div className={s.address_card_top_left}>Shipping address</div>
            {shippingAddresses ? (
              shippingAddresses?.length >= 1 ? (
                <></>
              ) : (
                <div
                  className={s.address_card_top_right}
                  onClick={() => setIsAddBillingAddress(!isAddBillingAddress)}
                >
                  + Add Address
                </div>
              )
            ) : (
              <></>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {shippingAddresses?.map(item => {
              const address = `${item?.country}. ${item?.city}, ${item?.state}, ${item?.street} ${item?.apartment}, ${item?.zip_code}`;
              return (
                <AddressCard
                  address={address}
                  number={item?.user?.phone}
                  isSelected={selectedShippingAddress === item?.id}
                  isEditing={editingShippingAddressId === item?.id}
                  onSelect={() => handleSelectBillingAddress(item?.id)}
                  onEdit={setEditingShippingAddressId}
                  item={item}
                  updateAddress={updateShippingAddress}
                  refetch={refetchShippingAddresses}
                />
              );
            })}

            {isAddBillingAddress && (
              <AddNewAddressForm
                refetch={refetchShippingAddresses}
                addAddress={addShippingAddress}
                hide={setIsAddBillingAddress}
              />
            )}
          </div>
        </div>

        <Promocode
          bookingItems={bookingItemsWithDiscount}
          onApplyDiscount={setBookingItemsWithDiscount}
        />
        <div className={s.checkout_subtotal}>
          {subtotals('Subtotal', subtotal)}
          {subtotals('VAT', 0)}
          {subtotals('Discount', discount)}
        </div>
        <Button
          wide
          darkGreen
          onClick={handleBuy}
          loading={isLoading}
          disabled={
            isLoading || !isUserDataComplete
            //|| !selectedShippingAddress
          }
          style={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            width: '91%',
          }}
        >
          Buy now {/*   also services for some reason */}
        </Button>
      </div>
    </>
  );
};

export default BookingCheckout;
